import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, API_BASE } from "@/lib/api";
import { FileText, ExternalLink, CalendarDays, Info } from "lucide-react";

const BACKEND = API_BASE.replace(/\/api$/, "");
const resolve = (link) => (link && link.startsWith("/api/") ? `${BACKEND}${link}` : link);

export default function Careers() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/recruitments").then((r) => setItems(r.data)).catch(() => {});
    api.get("/rank-lists").then((r) => setRanks(r.data)).catch(() => {});
  }, []);

  const shown = filter === "open" ? items.filter((i) => i.status === "open") : items;
  const openCount = items.filter((i) => i.status === "open").length;
  const rankItems = ranks.filter((r) => r.link);  // hide rank lists whose document is unavailable

  return (
    <div className="pb-24">
      <section className="border-b border-ink/10 py-20 circuit-bg-light" aria-labelledby="careers-title">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="tag mb-3">{t("careers.eyebrow")}</div>
          <h1 id="careers-title" className="font-display font-black text-brand text-4xl sm:text-5xl lg:text-6xl leading-[1]">
            {t("careers.title_a")} <span className="text-teal">{t("careers.title_b")}</span> {t("careers.title_c")}
          </h1>
          <p className="mt-6 text-ink/75 max-w-3xl leading-relaxed">{t("careers.intro")}</p>
        </div>
      </section>

      <section className="py-12" aria-label={t("nav.careers")}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="flex gap-2 mb-8" role="tablist" aria-label="Filter notifications">
              {[["all", t("careers.filter_all"), items.length], ["open", t("careers.filter_open"), openCount]].map(([k, label, n]) => (
                <button
                  key={k}
                  role="tab"
                  aria-selected={filter === k}
                  data-testid={`careers-filter-${k}`}
                  onClick={() => setFilter(k)}
                  className={`px-4 py-2 text-sm border transition-colors ${filter === k ? "bg-brand border-brand text-white" : "border-ink/15 text-ink/75 hover:border-brand hover:text-brand"}`}
                >
                  {label} <span className="font-mono text-xs opacity-80">({n})</span>
                </button>
              ))}
            </div>

            {shown.length === 0 ? (
              <div className="text-ink/60 font-mono text-sm" data-testid="careers-empty">{t("careers.no_items")}</div>
            ) : (
              <ol className="space-y-5 list-none p-0" data-testid="recruitment-list">
                {shown.map((n, i) => <NotificationCard key={n.id} n={n} index={i} t={t} />)}
              </ol>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 border border-ink/10 bg-surface p-6">
              <div className="flex items-center gap-2 tag mb-3"><Info className="w-3.5 h-3.5" aria-hidden="true" /> {t("careers.note_title")}</div>
              <p className="text-ink/75 text-sm leading-relaxed">{t("careers.note_body")}</p>
            </div>
          </aside>
        </div>
      </section>

      {rankItems.length > 0 && (
        <section className="py-12 border-t border-ink/10" aria-labelledby="ranklist-title" data-testid="ranklist-section">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="tag mb-3">{t("careers.ranklists_eyebrow")}</div>
            <h2 id="ranklist-title" className="font-display font-black text-brand text-3xl sm:text-4xl leading-tight">{t("careers.ranklists_title")}</h2>
            <p className="mt-3 text-ink/70 text-sm max-w-3xl leading-relaxed">{t("careers.ranklists_intro")}</p>
            <ol className="mt-8 space-y-3 list-none p-0" data-testid="ranklist-list">
              {rankItems.map((r) => (
                <li key={r.id} data-testid={`ranklist-${r.id}`}
                    className="bg-white border border-ink/10 border-l-4 border-l-teal p-5 flex flex-col sm:flex-row sm:items-center gap-4 card-hover">
                  <div className="flex-1">
                    <p className="text-ink/85 text-sm leading-relaxed">{r.title}</p>
                    {r.date && <span className="mt-1 inline-block font-mono text-[10px] uppercase tracking-widest text-ink/50">{r.date}</span>}
                  </div>
                  {r.link && (
                    <a href={resolve(r.link)} target="_blank" rel="noreferrer" data-testid={`ranklist-link-${r.id}`}
                       className="shrink-0 inline-flex items-center gap-2 border border-brand text-brand hover:bg-brand hover:text-white px-4 py-2.5 text-sm font-medium transition-colors">
                      <FileText className="w-4 h-4" aria-hidden="true" /> {t("careers.ranklist_view")}
                    </a>
                  )}
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}
    </div>
  );
}

function NotificationCard({ n, index, t }) {
  const isOpen = n.status === "open";
  const hasActions = n.notification_link || n.corrigendum_link || (isOpen && n.apply_url);
  return (
    <li>
      <article
        data-testid={`recruitment-${n.id}`}
        className={`bg-white border border-ink/10 p-6 lg:p-8 card-hover rise-in rise-in-${(index % 4) + 1} border-l-4 ${isOpen ? "border-l-teal" : "border-l-ink/20"}`}
      >
        <div className="flex items-center gap-3 flex-wrap mb-3 font-mono text-[10px] uppercase tracking-widest">
          <span data-testid={`recruitment-status-${n.id}`} className={`px-2 py-0.5 ${isOpen ? "bg-teal-light text-teal-dark" : "bg-ink/5 text-ink/70"}`}>
            {isOpen ? t("careers.open") : t("careers.closed")}
          </span>
          {n.posted_at && (
            <span className="flex items-center gap-1.5 text-ink/60"><CalendarDays className="w-3.5 h-3.5" aria-hidden="true" /> {t("careers.posted")} {n.posted_at}</span>
          )}
        </div>
        <h2 className="font-display font-bold text-brand text-xl lg:text-2xl leading-snug">{n.title}</h2>
        <p className="mt-3 text-ink/75 text-sm leading-relaxed">{n.description}</p>

        {n.positions?.length > 0 && (
          <div className="mt-5">
            <div className="tag mb-2">{t("careers.posts")}</div>
            <ol className="grid sm:grid-cols-2 gap-x-6 gap-y-1 list-none p-0">
              {n.positions.map((p, i) => (
                <li key={p || i} className="flex gap-2 text-sm text-ink/85">
                  <span className="font-mono text-[11px] text-teal pt-0.5">{String(i + 1).padStart(2, "0")}</span>
                  <span>{p}</span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {hasActions && (
        <div className="mt-6 pt-5 border-t border-ink/10 flex flex-wrap gap-3">
          {n.notification_link && (
            <a href={resolve(n.notification_link)} target="_blank" rel="noreferrer" data-testid={`recruitment-notification-${n.id}`}
               className="inline-flex items-center gap-2 border border-brand text-brand hover:bg-brand hover:text-white px-4 py-2.5 text-sm font-medium transition-colors">
              <FileText className="w-4 h-4" aria-hidden="true" /> {t("careers.notification")}
            </a>
          )}
          {n.corrigendum_link && (
            <a href={resolve(n.corrigendum_link)} target="_blank" rel="noreferrer" data-testid={`recruitment-corrigendum-${n.id}`}
               className="inline-flex items-center gap-2 border border-amber text-ink hover:bg-amber px-4 py-2.5 text-sm font-medium transition-colors">
              <FileText className="w-4 h-4" aria-hidden="true" /> {t("careers.corrigendum")}
            </a>
          )}
          {isOpen && n.apply_url && (
            <a href={n.apply_url} target="_blank" rel="noreferrer" data-testid={`recruitment-apply-${n.id}`}
               className="inline-flex items-center gap-2 bg-teal hover:bg-teal-dark text-white px-5 py-2.5 text-sm font-medium transition-colors ml-auto">
              {t("careers.apply")} <ExternalLink className="w-4 h-4" aria-hidden="true" />
            </a>
          )}
        </div>
        )}
      </article>
    </li>
  );
}
